import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  CallToActionSliceDefault,
  CallToActionSliceHighlighted,
  CallToActionSliceWithBackground,
  CallToActionSliceWithBackgroundAndSubtitle,
  CallToActionSliceWithImage,
} from '../../../prismicio-types';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PlainRightArrow } from '@/assets/PlainRightArrow';

enum SLICE_VARIATION {
  default = 'default',
  withImage = 'withImage',
  highlighted = 'highlighted',
  withBackground = 'withBackground',
  withBackgroundAndSubtitle = 'withBackgroundAndSubtitle',
}

/**
 * Props for `CallToAction`.
 */
export type CallToActionProps = SliceComponentProps<Content.CallToActionSlice>;

/**
 * Component for "CallToAction" Slices.
 */
const CallToAction = ({ slice }: CallToActionProps): JSX.Element => {
  const styledClass =
    slice.variation === SLICE_VARIATION.withBackground ? '' : 'background';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={styledClass}
    >
      {slice.variation === SLICE_VARIATION.default && (
        <DefaultCTA slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withImage && (
        <CTAWithImage slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.highlighted && (
        <HighlightedCTA slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withBackground && (
        <CTAWithBackground slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withBackgroundAndSubtitle && (
        <CTAWithBackgroundAndSubtitle slice={slice} />
      )}
    </section>
  );
};

const DefaultCTA = ({
  slice,
}: {
  slice: CallToActionSliceDefault;
}): JSX.Element => {
  const {
    primary: {
      button_label: buttonLabel,
      button_link: buttonLink,
      description,
      title,
      slice_ref: sliceRef,
    },
  } = slice;
  return (
    <div className="px-5 md:px-16 py-16 lg:py-28 text-white max-w-screen-2xl mx-auto">
      <div className="text-center flex flex-col items-center gap-y-6">
        <div className="max-w-3xl break-words flex flex-col gap-y-6">
          <PrismicRichText field={title} />
          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }) => (
                <p className="text-medium-normal">{children}</p>
              ),
            }}
          />
        </div>
        {buttonLabel && (
          <>
            {sliceRef ? (
              <a className="glassy-button" href={`#${sliceRef}`}>
                {buttonLabel}
              </a>
            ) : (
              <PrismicNextLink field={buttonLink} className="glassy-button">
                <span>{buttonLabel}</span>
              </PrismicNextLink>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CTAWithImage = ({
  slice,
}: {
  slice: CallToActionSliceWithImage;
}): JSX.Element => {
  const {
    primary: {
      button_label: buttonLabel,
      button_link: buttonLink,
      description,
      image,
      title,
    },
  } = slice;
  return (
    <div className="px-5 md:px-16 py-16 lg:py-28 text-white max-w-screen-2xl mx-auto">
      <div className="border-solid border-button-border border-[1px] flex flex-col md:flex-row items-center rounded-[20px]">
        <div className="p-8 md:p-12 flex flex-col gap-y-6 md:w-1/2">
          <PrismicRichText field={title} />
          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }) => (
                <p className="text-medium-normal">{children}</p>
              ),
            }}
          />
          <PrismicNextLink field={buttonLink} className="glassy-button">
            <span>{buttonLabel}</span>
          </PrismicNextLink>
        </div>
        <div className="md:w-1/2 flex md:justify-end">
          <PrismicNextImage
            field={image}
            className="rounded-b-[20px] md:rounded-r-[20px] md:rounded-b-none"
          />
        </div>
      </div>
    </div>
  );
};

const HighlightedCTA = ({
  slice,
}: {
  slice: CallToActionSliceHighlighted;
}): JSX.Element => {
  const {
    primary: {
      button_label: buttonLabel,
      button_link: buttonLink,
      description,
      title,
      slice_ref: sliceRef,
    },
  } = slice;
  return (
    <div className="bg-white bg-opacity-10 border-t border-b border-solid border-white border-opacity-15">
      <div className="px-5 md:px-16 py-16 lg:py-28 text-white max-w-screen-2xl mx-auto">
        <div className="text-center flex flex-col items-center gap-y-6">
          <div className="max-w-4xl break-words flex flex-col gap-y-6">
            <PrismicRichText field={title} />
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-medium-normal">{children}</p>
                ),
              }}
            />
          </div>
          {buttonLabel && (
            <>
              {sliceRef ? (
                <a className="glassy-button" href={`#${sliceRef}`}>
                  {buttonLabel}
                </a>
              ) : (
                <PrismicNextLink field={buttonLink} className="glassy-button">
                  <span>{buttonLabel}</span>
                </PrismicNextLink>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CTAWithBackground = ({
  slice,
}: {
  slice: CallToActionSliceWithBackground;
}): JSX.Element => {
  const {
    primary: {
      button_label: buttonLabel,
      button_link: buttonLink,
      title,
      slice_ref: sliceRef,
      background,
    },
  } = slice;
  return (
    <div
      className="relative px-5 md:px-16 py-16 lg:py-28 text-white max-w-screen-2xl mx-auto overflow-hidden"
      style={{
        backgroundImage: background?.url ? `url(${background.url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {background?.url && (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-0" />
      )}
      <div className="relatve z-10 text-center flex flex-col items-center gap-y-6">
        <div className="w-full max-w-[819px] text-center">
          <span className="text-2xl sm:text-3xl md:text-4xl font-semibold leading-snug md:leading-[48px]">
            <PrismicRichText field={title} />
          </span>
        </div>

        {buttonLabel && (
          <>
            {sliceRef ? (
              <a className="glassy-button" href={`#${sliceRef}`}>
                {buttonLabel}
              </a>
            ) : (
              <PrismicNextLink
                field={buttonLink}
                className="glassy-button inline-flex items-center justify-center"
              >
                <span className="flex items-center gap-2">
                  {buttonLabel}
                  <PlainRightArrow
                    className="ml-2"
                    height={24}
                    width={24}
                    arrowFill="white"
                  />
                </span>
              </PrismicNextLink>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CTAWithBackgroundAndSubtitle = ({
  slice,
}: {
  slice: CallToActionSliceWithBackgroundAndSubtitle;
}): JSX.Element => {
  const {
    primary: {
      button_label: buttonLabel,
      button_link: buttonLink,
      title,
      description,
      slice_ref: sliceRef,
      background,
    },
  } = slice;
  return (
    <div
      className="relative px-5 md:px-16 py-16 lg:py-28 text-white mx-auto overflow-hidden"
      style={{
        backgroundImage: background?.url ? `url(${background.url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {background?.url && (
        <>
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-70 z-0" />

          {/* Gradiente superior */}
          <div className="absolute top-0 left-0 w-full h-32 md:h-48 z-10 bg-gradient-to-b from-[#0E1010] to-transparent pointer-events-none" />

          {/* Gradiente inferior */}
          <div className="absolute bottom-0 left-0 w-full h-32 md:h-48 z-10 bg-gradient-to-t from-[#0E1010] to-transparent pointer-events-none" />
        </>
      )}
      <div className="relative z-10 text-center flex flex-col items-center gap-y-6">
        <div className="w-full max-w-[819px] text-center flex flex-col gap-y-4">
          <span className="text-2xl sm:text-3xl md:text-4xl font-semibold leading-snug md:leading-[48px]">
            <PrismicRichText field={title} />
          </span>
          {description && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                    {children}
                  </p>
                ),
              }}
            />
          )}
        </div>

        {buttonLabel && (
          <>
            {sliceRef ? (
              <a className="glassy-button" href={`#${sliceRef}`}>
                {buttonLabel}
              </a>
            ) : (
              <PrismicNextLink
                field={buttonLink}
                className="glassy-button inline-flex items-center justify-center"
              >
                <span className="flex items-center gap-2">
                  {buttonLabel}
                  <PlainRightArrow
                    className="ml-2"
                    height={24}
                    width={24}
                    arrowFill="white"
                  />
                </span>
              </PrismicNextLink>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CallToAction;
